import { useState } from 'react';
import { Photo, usePhotoGalleryContainer } from '..';
import styles from './styles/PhotoGalleryResolutionSelect.module.scss';

type OptionType = 'web' | 'print';

interface ResolutionDetailProps {
  resolution: string;
  dpi: string;
  description: string;
  isHighResolution: boolean;
}

interface ResolutionOptionProps {
  type: OptionType;
  price: string;
  detail: ResolutionDetailProps;
  selected: boolean;
  onSelect: (type: OptionType) => void;
}

function ResolutionDetail({ resolution, dpi, description }: ResolutionDetailProps) {
  return (
    <div className={styles.ResolutionDetail}>
      <span className={styles.ResolutionText}>{`${resolution} (${dpi})`}</span>
      <span className={styles.DescriptionText}>{description}</span>
    </div>
  );
}

function ResolutionOption({ type, price, detail, selected, onSelect }: ResolutionOptionProps) {
  return (
    <div className={styles.Option} onClick={() => onSelect(type)}>
      <div className={styles.ResolutionPriceText}>
        <input type="radio" name="resolution" checked={selected} onChange={() => onSelect(type)} id={type} />
        <label htmlFor={type} className={styles.Label}>
          {type.charAt(0).toUpperCase() + type.slice(1)} Resolution
        </label>
        <span className={styles.Price}>{price}</span>
      </div>
      {<ResolutionDetail {...detail} />}
    </div>
  );
}

export default function PhotoGalleryResolutionSelect() {
  const [{ photoGallery, currentIndex, photoSlides }, { setCurrentSelection }] = usePhotoGalleryContainer();
  const [selectedOption, setSelectedOption] = useState<OptionType>('print');

  const photo = photoSlides[currentIndex] as Photo;
  const highResolutionDisplayPrice = photo.highResolutionDisplayPrice ?? photoGallery.highResolutionDisplayPrice;
  const lowResolutionDisplayPrice = photo.lowResolutionDisplayPrice ?? photoGallery.lowResolutionDisplayPrice;
  const highResolutionPriceInCents = photo.highResolutionPriceInCents ?? photoGallery.highResolutionPriceInCents;
  const lowResolutionPriceInCents = photo.lowResolutionPriceInCents ?? photoGallery.lowResolutionPriceInCents;

  const options = {
    web: {
      displayPrice: lowResolutionDisplayPrice,
      price: lowResolutionPriceInCents,
      detail: {
        resolution: '1024 x 756 px',
        dpi: '72 dpi',
        description: 'Best for slideshow and web use',
        isHighResolution: false
      }
    },
    print: {
      displayPrice: highResolutionDisplayPrice,
      price: highResolutionPriceInCents,
      detail: {
        resolution: '3000 x 2216 px',
        dpi: '300 dpi',
        description: 'Best for printing 8x10, 16x20 or 20x30',
        isHighResolution: true
      }
    }
  };

  function handleSelect(type: OptionType) {
    setSelectedOption(type);
    const selectedDetails = options[type].detail;
    const selectedDisplayPrice = options[type].displayPrice;
    const selectedPrice = options[type].price;
    const isHighResolution = type === 'print';

    setCurrentSelection({
      resolution: `${selectedDetails.resolution} (${selectedDetails.dpi})`,
      displayPrice: selectedDisplayPrice,
      price: selectedPrice,
      isHighResolution
    });
  }

  return (
    <div className={styles.ResolutionSelector}>
      {Object.entries(options).map(([key, value]) => (
        <ResolutionOption
          key={key}
          type={key as OptionType}
          price={value.displayPrice}
          detail={value.detail}
          selected={selectedOption === key}
          onSelect={handleSelect}
        />
      ))}
    </div>
  );
}
